import * as XLSX from 'xlsx';
import {
  getCompareTankSlotsForVessel,
  VesselTankSlot,
} from '../constants/cargo-heating-vessel-rules';

export const VOYAGE_LOG_UPLOAD_EXTENSIONS = ['xls', 'xlsx', 'xlsm', 'csv'] as const;
export const VOYAGE_LOG_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const VOYAGE_LOG_VALIDATION_ERROR_LIMIT = 15;

export interface VoyageLogUploadValidationOptions {
  vesselName?: string;
  vesselImo?: string;
}

export interface VoyageLogUploadValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  rowCount: number;
}

type CanonicalField =
  | 'record_date'
  | 'record_time'
  | 'wind_direction_deg'
  | 'wind_speed_kn'
  | 'air_temperature_c'
  | 'seawater_temperature_c'
  | 'heating_duration_hr'
  | 'bc_total_hfo_mt'
  | 'bc_total_mgo_mt';

interface ParsedColumn {
  index: number;
  kind: 'date' | 'field' | 'tank';
  field?: CanonicalField;
  tankKey?: string;
}

const TANK_HEADER_RE =
  /(SLOP\s*[-–]?\s*(?:PORT|STBD|PTR2?|PTR)|(?:TANK\s*)?\d+\s*[-–]?\s*(?:PORT|STBD|P|S|PTR2?|PTR))/i;

const DATE_HEADER_RE = /^(record[_\s-]?date|date|log\s*date)$/i;

const SKIP_COLUMN_HEADER_RE =
  /weighted|air\s*temp|sea\s*temp|seawater|wind|boiler|latitude|longitude|circulation|total\s*load|bunker|heating\s*duration|remarks?/i;

const FIELD_HEADER_MAP: Array<{ field: CanonicalField; pattern: RegExp }> = [
  { field: 'record_date', pattern: DATE_HEADER_RE },
  { field: 'record_time', pattern: /^(record[_\s-]?time|time)$/i },
  {
    field: 'wind_direction_deg',
    pattern: /^(wind[_\s-]?direction(\s*\(deg\))?|wind\s*dir(\s*deg)?)$/i,
  },
  {
    field: 'wind_speed_kn',
    pattern: /^(wind[_\s-]?speed(\s*\(kn\))?|wind\s*speed)$/i,
  },
  {
    field: 'air_temperature_c',
    pattern: /^(air[_\s-]?(temp(erature)?)(\s*\(c\))?|air\s*temp)$/i,
  },
  {
    field: 'seawater_temperature_c',
    pattern: /^(sea(water)?[_\s-]?(temp(erature)?)(\s*\(c\))?|water\s*temp)$/i,
  },
  {
    field: 'heating_duration_hr',
    pattern: /^(heating[_\s-]?(duration|hours?)(\s*\(hr\))?|heating\s*hrs?)$/i,
  },
  {
    field: 'bc_total_hfo_mt',
    pattern: /^(bc[_\s-]?total[_\s-]?hfo|bunker[_\s-]?hfo(\s*\(mt\))?|hfo(\s*\(mt\))?)$/i,
  },
  {
    field: 'bc_total_mgo_mt',
    pattern: /^(bc[_\s-]?total[_\s-]?mgo|bunker[_\s-]?mgo(\s*\(mt\))?|mgo(\s*\(mt\))?)$/i,
  },
];

const NUMERIC_FIELD_RULES: Record<
  CanonicalField,
  { min?: number; max?: number; label: string } | null
> = {
  record_date: null,
  record_time: null,
  wind_direction_deg: { min: 0, max: 360, label: 'Wind direction (deg)' },
  wind_speed_kn: { min: 0, max: 120, label: 'Wind speed (kn)' },
  air_temperature_c: { min: -50, max: 150, label: 'Air temperature (°C)' },
  seawater_temperature_c: { min: -5, max: 45, label: 'Seawater temperature (°C)' },
  heating_duration_hr: { min: 0, max: 24, label: 'Heating duration (hr)' },
  bc_total_hfo_mt: { min: 0, max: 10000, label: 'Bunker HFO (MT)' },
  bc_total_mgo_mt: { min: 0, max: 10000, label: 'Bunker MGO (MT)' },
};

function cellStr(row: unknown[] | undefined, col: number): string {
  if (!row || col < 0) {
    return '';
  }
  return String(row[col] ?? '').trim();
}

function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return (parts.length > 1 ? parts.pop() : '')?.toLowerCase() || '';
}

function normalizeTankKey(raw: string): string | null {
  const compact = raw.replace(/\s+/g, ' ').trim().toUpperCase();
  const match = compact.match(TANK_HEADER_RE);
  if (!match) {
    return null;
  }

  let token = match[1].replace(/\s+/g, '').replace(/–/g, '-').toUpperCase();
  token = token.replace(/^TANK/, '');

  if (token.startsWith('SLOP')) {
    if (token.includes('STBD') || /\bS\b/.test(token)) {
      return 'SLOP-STBD';
    }
    if (token.includes('PTR2')) {
      return 'SLOP-PTR2';
    }
    if (token.includes('PTR')) {
      return 'SLOP-PTR';
    }
    return 'SLOP-PORT';
  }

  token = token.replace(/-P(?=$|-)/, '-PORT').replace(/-S(?=$|-)/, '-STBD');

  const parts = token.match(/^(\d+)-(PORT|STBD|PTR2|PTR)$/);
  return parts ? `${parts[1]}-${parts[2]}` : null;
}

function maxColumnCount(rows: unknown[][], rowIndices: number[]): number {
  return Math.max(
    0,
    ...rowIndices.map((rowIndex) => (rows[rowIndex] as unknown[] | undefined)?.length ?? 0),
  );
}

function findTankOnColumn(
  rows: unknown[][],
  headerRowIndices: number[],
  columnIndex: number,
): string | null {
  for (const rowIndex of headerRowIndices) {
    for (const offset of [0, -1, 1, -2, 2]) {
      const text = cellStr(rows[rowIndex] as unknown[], columnIndex + offset);
      if (!text) {
        continue;
      }
      const key = normalizeTankKey(text);
      if (key) {
        return key;
      }
    }
  }
  return null;
}

function buildHeaderRowIndices(rows: unknown[][], dateRowIndex: number): number[] {
  const indices: number[] = [];
  for (
    let rowIndex = Math.max(0, dateRowIndex - 6);
    rowIndex <= Math.min(rows.length - 1, dateRowIndex + 4);
    rowIndex++
  ) {
    indices.push(rowIndex);
  }
  return indices;
}

function inferNumericDataColumns(
  rows: unknown[][],
  dateRowIndex: number,
  dateColumnIndex: number,
): number[] {
  const maxCol = maxColumnCount(rows, buildHeaderRowIndices(rows, dateRowIndex));
  const inferred: number[] = [];

  for (let columnIndex = dateColumnIndex + 1; columnIndex < maxCol; columnIndex++) {
    const headerText = buildHeaderRowIndices(rows, dateRowIndex)
      .map((rowIndex) => cellStr(rows[rowIndex] as unknown[], columnIndex))
      .join(' ');
    if (SKIP_COLUMN_HEADER_RE.test(headerText)) {
      continue;
    }

    let hits = 0;
    let rowsChecked = 0;
    for (let rowIndex = dateRowIndex + 1; rowIndex < Math.min(rows.length, dateRowIndex + 25); rowIndex++) {
      const row = rows[rowIndex] as unknown[] | undefined;
      if (!row || !parseDateValue(row[dateColumnIndex])) {
        continue;
      }
      rowsChecked++;
      const value = toNumber(row[columnIndex]);
      if (value !== null && value >= -50 && value <= 150) {
        hits++;
      }
    }

    if (rowsChecked >= 1 && hits >= Math.max(1, Math.ceil(rowsChecked * 0.3))) {
      inferred.push(columnIndex);
    }
  }

  return inferred;
}

function normalizeHeaderLabel(value: unknown): string {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

function toNumber(value: unknown): number | null {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '-' || /^x$/i.test(raw)) {
    return null;
  }
  const parsed = Number(raw.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDateValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number' && value > 30000 && value < 70000) {
    const date = new Date((value - 25569) * 86400000);
    if (!Number.isNaN(date.getTime())) {
      return formatIsoDate(date);
    }
  }

  const text = String(value).trim();
  const dmy = text.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (dmy) {
    const date = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    if (!Number.isNaN(date.getTime())) {
      return formatIsoDate(date);
    }
  }

  const ymd = text.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/);
  if (ymd) {
    const date = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(date.getTime())) {
      return formatIsoDate(date);
    }
  }

  const parsed = Date.parse(text);
  if (Number.isFinite(parsed)) {
    return formatIsoDate(new Date(parsed));
  }

  return null;
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function findHeaderRowIndex(rows: unknown[][]): number {
  for (let rowIndex = 0; rowIndex < Math.min(rows.length, 100); rowIndex++) {
    const row = rows[rowIndex] as unknown[] | undefined;
    if (!row) {
      continue;
    }

    for (const cell of row) {
      const label = normalizeHeaderLabel(cell);
      if (DATE_HEADER_RE.test(label) || /^date\b/i.test(label)) {
        return rowIndex;
      }
    }
  }
  return -1;
}

function findDateColumnIndex(rows: unknown[][], headerRowIndices: number[]): number {
  const maxCol = maxColumnCount(rows, headerRowIndices);
  for (let columnIndex = 0; columnIndex < maxCol; columnIndex++) {
    for (const rowIndex of headerRowIndices) {
      const label = normalizeHeaderLabel(cellStr(rows[rowIndex] as unknown[], columnIndex));
      if (DATE_HEADER_RE.test(label) || /^date\b/i.test(label)) {
        return columnIndex;
      }
    }
  }
  return 0;
}

function parseColumns(
  rows: unknown[][],
  headerRowIndex: number,
): { columns: ParsedColumn[]; dateColumnIndex: number } {
  const headerRowIndices = buildHeaderRowIndices(rows, headerRowIndex);
  const headerRow = rows[headerRowIndex] as unknown[];
  const dateColumnIndex = findDateColumnIndex(rows, headerRowIndices);
  const maxCol = Math.max(headerRow?.length ?? 0, maxColumnCount(rows, headerRowIndices));
  const columns: ParsedColumn[] = [];
  const tankColumnsByIndex = new Map<number, string>();

  columns.push({ index: dateColumnIndex, kind: 'date' });

  for (let columnIndex = 0; columnIndex < maxCol; columnIndex++) {
    if (columnIndex === dateColumnIndex) {
      continue;
    }

    const label = normalizeHeaderLabel(headerRow?.[columnIndex]);
    if (label) {
      for (const mapping of FIELD_HEADER_MAP) {
        if (mapping.field !== 'record_date' && mapping.pattern.test(label)) {
          columns.push({ index: columnIndex, kind: 'field', field: mapping.field });
          break;
        }
      }
    }

    const tankKey = findTankOnColumn(rows, headerRowIndices, columnIndex);
    if (tankKey && !tankColumnsByIndex.has(columnIndex)) {
      tankColumnsByIndex.set(columnIndex, tankKey);
      columns.push({ index: columnIndex, kind: 'tank', tankKey });
    }
  }

  return { columns, dateColumnIndex };
}

function slotMatchesTankKey(slot: VesselTankSlot, tankKey: string): boolean {
  const normalizedSlot = slot.slot.toUpperCase();
  const normalizedKey = tankKey.toUpperCase();
  if (normalizedSlot === normalizedKey) {
    return true;
  }

  const slotLabel = slot.label.replace(/\s+/g, ' ').trim().toUpperCase();
  const keyAsLabel = normalizedKey.replace(/-/g, ' ');
  return slotLabel === keyAsLabel;
}

function readWorkbookRows(file: File): Promise<unknown[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const buffer = reader.result as ArrayBuffer;
        const workbook = XLSX.read(buffer, { type: 'array', raw: true });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          resolve([]);
          return;
        }

        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
          raw: true,
        }) as unknown[][];

        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

export function validateVoyageLogUploadFormat(
  file: File | null | undefined,
): VoyageLogUploadValidationResult {
  const errors: string[] = [];

  if (!file) {
    return { valid: false, errors: ['No file selected.'], warnings: [], rowCount: 0 };
  }

  const extension = getFileExtension(file.name);
  if (!VOYAGE_LOG_UPLOAD_EXTENSIONS.includes(extension as (typeof VOYAGE_LOG_UPLOAD_EXTENSIONS)[number])) {
    errors.push(
      `Invalid file type ".${extension || 'unknown'}". Allowed: ${VOYAGE_LOG_UPLOAD_EXTENSIONS.join(', ')}.`,
    );
  }

  if (!file.name.trim()) {
    errors.push('File name is missing.');
  }

  if (file.size <= 0) {
    errors.push('File is empty.');
  }

  if (file.size > VOYAGE_LOG_UPLOAD_MAX_BYTES) {
    errors.push(
      `File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds the ${VOYAGE_LOG_UPLOAD_MAX_BYTES / (1024 * 1024)} MB limit.`,
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings: [],
    rowCount: 0,
  };
}

export async function validateVoyageLogUploadContent(
  file: File,
  options: VoyageLogUploadValidationOptions = {},
): Promise<VoyageLogUploadValidationResult> {
  const formatResult = validateVoyageLogUploadFormat(file);
  if (!formatResult.valid) {
    return formatResult;
  }

  const errors: string[] = [];
  const warnings: string[] = [];
  let totalErrorCount = 0;

  const pushError = (message: string): void => {
    totalErrorCount++;
    if (errors.length < VOYAGE_LOG_VALIDATION_ERROR_LIMIT) {
      errors.push(message);
    }
  };

  let rows: unknown[][];
  try {
    rows = await readWorkbookRows(file);
  } catch {
    return {
      valid: false,
      errors: ['Unable to read the spreadsheet. Ensure the file is a valid Excel or CSV workbook.'],
      warnings: [],
      rowCount: 0,
    };
  }

  if (!rows.length) {
    return {
      valid: false,
      errors: ['Workbook is empty.'],
      warnings: [],
      rowCount: 0,
    };
  }

  const headerRowIndex = findHeaderRowIndex(rows);
  if (headerRowIndex < 0) {
    return {
      valid: false,
      errors: ['Missing required "Date" column header.'],
      warnings: [],
      rowCount: 0,
    };
  }

  const { columns, dateColumnIndex } = parseColumns(rows, headerRowIndex);
  const dateColumn = columns.find((column) => column.kind === 'date');

  if (!dateColumn) {
    return {
      valid: false,
      errors: ['Missing required "Date" column header.'],
      warnings: [],
      rowCount: 0,
    };
  }

  const fieldColumns = columns.filter(
    (column): column is ParsedColumn & { field: CanonicalField } =>
      column.kind === 'field' && !!column.field,
  );
  let tankColumns = columns.filter(
    (column): column is ParsedColumn & { tankKey: string } =>
      column.kind === 'tank' && !!column.tankKey,
  );

  if (!tankColumns.length) {
    const inferredIndexes = inferNumericDataColumns(rows, headerRowIndex, dateColumnIndex);
    tankColumns = inferredIndexes.map((index, offset) => ({
      index,
      kind: 'tank' as const,
      tankKey: `COL-${offset + 1}`,
    }));
  }

  const hasRecognizableData = tankColumns.length > 0 || fieldColumns.length > 0;
  if (!hasRecognizableData) {
    warnings.push(
      'Could not verify tank or metric columns in this layout. The server will validate the file on upload.',
    );
  }

  const expectedTankSlots = getCompareTankSlotsForVessel(
    options.vesselName,
    options.vesselImo,
  );
  const foundTankKeys = new Set(
    tankColumns.map((column) => column.tankKey).filter((key) => !key.startsWith('COL-')),
  );
  const missingTankSlots = expectedTankSlots.filter(
    (slot) => ![...foundTankKeys].some((key) => slotMatchesTankKey(slot, key)),
  );

  if (!tankColumns.length) {
    warnings.push(
      'Tank column headers were not detected (e.g. 1 Port, 1 Stbd). Upload will proceed and the backend will parse the file.',
    );
  } else if (foundTankKeys.size > 0 && missingTankSlots.length === expectedTankSlots.length) {
    warnings.push(
      'Tank columns do not exactly match this vessel layout. Verify Port/Stbd/Ptr headers if needed.',
    );
  } else if (missingTankSlots.length > 0 && foundTankKeys.size > 0) {
    const labels = missingTankSlots.map((slot) => slot.label).join(', ');
    warnings.push(`Some expected tank columns were not found: ${labels}.`);
  }

  const validateRowMetrics = tankColumns.some((column) => !column.tankKey.startsWith('COL-'));

  const seenDates = new Set<string>();
  let dataRowCount = 0;
  const metricColumns = [...tankColumns, ...fieldColumns];

  for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex] as unknown[] | undefined;
    if (!row) {
      continue;
    }

    const dateValue = row[dateColumn.index];
    const isoDate = parseDateValue(dateValue);
    const hasOtherValues = metricColumns.some((column) => {
      return String(row[column.index] ?? '').trim() !== '';
    });

    if (!isoDate && !hasOtherValues) {
      continue;
    }

    if (!isoDate) {
      pushError(`Row ${rowIndex + 1}: invalid or missing date.`);
      continue;
    }

    if (seenDates.has(isoDate)) {
      pushError(`Row ${rowIndex + 1}: duplicate date ${isoDate}.`);
    } else {
      seenDates.add(isoDate);
    }

    dataRowCount++;

    if (!validateRowMetrics) {
      continue;
    }

    let rowHasMetric = false;

    for (const column of tankColumns) {
      if (column.tankKey.startsWith('COL-')) {
        const raw = row[column.index];
        if (String(raw ?? '').trim() !== '' && toNumber(raw) !== null) {
          rowHasMetric = true;
        }
        continue;
      }

      const raw = row[column.index];
      if (String(raw ?? '').trim() === '') {
        continue;
      }
      const value = toNumber(raw);
      if (value === null) {
        pushError(
          `Row ${rowIndex + 1} (${isoDate}), ${column.tankKey}: temperature must be numeric.`,
        );
        continue;
      }
      if (value < -50 || value > 150) {
        pushError(
          `Row ${rowIndex + 1} (${isoDate}), ${column.tankKey}: temperature ${value}°C is out of range (-50 to 150).`,
        );
        continue;
      }
      rowHasMetric = true;
    }

    for (const column of fieldColumns) {
      const raw = row[column.index];
      if (String(raw ?? '').trim() === '') {
        continue;
      }

      const rules = NUMERIC_FIELD_RULES[column.field];
      if (!rules) {
        rowHasMetric = true;
        continue;
      }

      const value = toNumber(raw);
      if (value === null) {
        pushError(
          `Row ${rowIndex + 1} (${isoDate}), ${rules.label}: value must be numeric.`,
        );
        continue;
      }

      if (
        (rules.min != null && value < rules.min) ||
        (rules.max != null && value > rules.max)
      ) {
        pushError(
          `Row ${rowIndex + 1} (${isoDate}), ${rules.label}: ${value} is out of allowed range (${rules.min}–${rules.max}).`,
        );
        continue;
      }

      rowHasMetric = true;
    }

    if (validateRowMetrics && !rowHasMetric) {
      pushError(`Row ${rowIndex + 1} (${isoDate}): no valid temperature or voyage metrics.`);
    }
  }

  if (dataRowCount === 0) {
    pushError('No data rows found below the header row.');
  }

  if (totalErrorCount > VOYAGE_LOG_VALIDATION_ERROR_LIMIT) {
    errors.push(
      `…and ${totalErrorCount - VOYAGE_LOG_VALIDATION_ERROR_LIMIT} more issue(s).`,
    );
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    rowCount: dataRowCount,
  };
}

export async function validateVoyageLogUpload(
  file: File,
  options: VoyageLogUploadValidationOptions = {},
): Promise<VoyageLogUploadValidationResult> {
  return validateVoyageLogUploadContent(file, options);
}
